import { InputValidationType } from '$lib/types/Form';

import { isValidPhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js/max';

import { parseCountryCodesInPartialNumber } from './helper';
import translations from './translations';

import type { Language } from '$lib/types/Language';

export const enum ValidationError {
    Required = 'Required',
    EmptyText = 'EmptyText',
    InvalidNumber = 'InvalidNumber',
    PhoneTooLong = 'PhoneTooLong',
    PhoneTooShort = 'PhoneTooShort',
    InvalidPhone = 'InvalidPhone',
    InvalidEmail = 'InvalidEmail',
    InvalidUrl = 'InvalidUrl',
    InvalidBirthday = 'InvalidBirthday',
    PublicEmail = 'PublicEmail',
}

export const getTranslatedValidationError = (error: ValidationError, language: Language) => {
    return translations[language].validation[error] ?? error;
};

export const validationMap = {
    [InputValidationType.REQUIRED]: (value: string, type: InputValidationType) => {
        if (!value) {
            return ValidationError.Required;
        }

        // For phoneV2 types only
        // If value is only the phone code and the input is required, consider the field empty
        if (
            type === InputValidationType.PHONE_V2 &&
            parseCountryCodesInPartialNumber(value)?.isPhoneCodeOnly
        ) {
            return ValidationError.Required;
        }
    },
    [InputValidationType.TEXT]: (value: string) => {
        if (value && !value.length) {
            return ValidationError.EmptyText;
        }
    },
    [InputValidationType.NUMBER]: (value: string) => {
        if (value && !value.match(/^[0-9]*$/)) {
            return ValidationError.InvalidNumber;
        }
    },
    [InputValidationType.PHONE]: (value: string) => {
        if (value && !value.match(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/)) {
            return ValidationError.InvalidPhone;
        }
    },
    [InputValidationType.PHONE_V2]: (value: string) => {
        if (!value || parseCountryCodesInPartialNumber(value)?.isPhoneCodeOnly) {
            // Only validate if there is something
            // If the field is required it will be validated by .REQUIRED
            // Additionally, if the value is only the phone code, this will be handled by .REQUIRED
            // This is needed here to not trigger the phone code validation if the field
            // is prefilled with the phone code of a country.
            return;
        }

        const lengthError = validatePhoneNumberLength(value);

        if (lengthError === 'TOO_LONG') {
            return ValidationError.PhoneTooLong;
        }

        if (lengthError === 'TOO_SHORT') {
            return ValidationError.PhoneTooShort;
        }

        if (!isValidPhoneNumber(value)) {
            return ValidationError.InvalidPhone;
        }
    },
    [InputValidationType.EMAIL]: (value: string) => {
        if (
            value &&
            !value.match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            )
        ) {
            return ValidationError.InvalidEmail;
        }
    },
    [InputValidationType.URL]: (value: string) => {
        if (
            value &&
            !value.match(/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/)
        ) {
            return ValidationError.InvalidUrl;
        }
    },
    [InputValidationType.TEXTAREA]: (value: string) => {
        if (value && !value.length) {
            return ValidationError.EmptyText;
        }
    },
    [InputValidationType.BIRTHDAY]: (value: string) => {
        if (value === 'invalid') {
            return ValidationError.InvalidBirthday;
        }
    },
};

const PUBLIC_EMAIL_DOMAINS = new Set([
    // Google
    'gmail.com',
    'googlemail.com',
    // Yahoo
    'yahoo.com',
    'yahoo.co.uk',
    'yahoo.co.in',
    'yahoo.ca',
    'yahoo.com.au',
    'yahoo.de',
    'yahoo.fr',
    'rocketmail.com',
    // Microsoft
    'hotmail.com',
    'hotmail.co.uk',
    'hotmail.de',
    'hotmail.ch',
    'outlook.com',
    'outlook.de',
    'live.com',
    'live.de',
    'live.co.uk',
    'live.at',
    'msn.com',
    // Apple
    'icloud.com',
    'me.com',
    'mac.com',
    // Other global
    'aol.com',
    'mail.com',
    'zoho.com',
    'yandex.com',
    'yandex.ru',
    'mail.ru',
    'inbox.com',
    // Privacy-focused
    'protonmail.com',
    'proton.me',
    'pm.me',
    'protonmail.ch',
    'tutanota.com',
    'tutanota.de',
    'tuta.io',
    'tuta.com',
    'posteo.de',
    'posteo.net',
    'mailbox.org',
    'fastmail.com',
    'hey.com',
    // Germany
    'gmx.com',
    'gmx.de',
    'gmx.net',
    'gmx.at',
    'gmx.ch',
    'web.de',
    't-online.de',
    'freenet.de',
    'arcor.de',
    '1und1.de',
    'online.de',
    'vodafone.de',
    // Austria
    'a1.net',
    'aon.at',
    'chello.at',
    'magenta.at',
    'drei.at',
    // Switzerland
    'bluewin.ch',
    'sunrise.ch',
    'hispeed.ch',
    // US ISPs
    'att.net',
    'sbcglobal.net',
    'comcast.net',
    'verizon.net',
    'cox.net',
    'charter.net',
    'earthlink.net',
]);

export const isPublicEmailDomain = (value: string): boolean => {
    const domain = value.split('@')[1]?.toLowerCase();

    return !!domain && PUBLIC_EMAIL_DOMAINS.has(domain);
};

export const composeValidation =
    (...validators: ((value: string) => ValidationError | undefined)[]) =>
    (value: string) => {
        return validators.reduce(
            (error: string | undefined, validator) => error || validator(value),
            undefined,
        );
    };
